import React, { useState, useEffect, useCallback } from "react";
import { Plus, Upload } from "lucide-react";
import { toast } from '../utils/toast';
import EmployeeList from "../components/employees/EmployeeList";
import EmployeeFormModal from "../components/employees/EmployeeFormModal";
import PageLayout from "../components/PageLayout";
import { EmployeeForm, EmployeesPageProps } from "../components/common/types";
import { getEmployeesPaginated, EmployeeServerFilters } from "../api/employee";
import { PaginationMeta } from "../api/doctor";
import EmployeeMain from "../types/employee";
import { useAuth } from "../context/AuthContext";
import { mapEmployeeMainToForm } from "../mappers/employee.mapper";


const EmployeesPage: React.FC<EmployeesPageProps> = ({ onLogout, onNavigate }) => {
  const [employees, setEmployees] = useState<EmployeeMain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<EmployeeForm | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [serverFilters, setServerFilters] = useState<EmployeeServerFilters>({});
  const { user } = useAuth()

  const loadEmployees = useCallback(async (page: number = 1, filters: EmployeeServerFilters = {}): Promise<void> => {
    if (!user || !user.tenant_id) return;

    setIsLoading(true);
    try {
      const result = await getEmployeesPaginated(user.tenant_id, page, 15, filters);
      setEmployees(result.data);
      setPagination(result.pagination);
    } catch (error) {
      console.log("error loadEmployee: ", error);
    }
    setIsLoading(false);
  }, [user]);

  const handleServerFilterChange = (filters: EmployeeServerFilters) => {
    setServerFilters(filters);
    loadEmployees(1, filters);
  };

  useEffect(() => {
    if (user && user.tenant_id) {
      loadEmployees();
    }
  }, [user, loadEmployees]);


  const handleEdit = (employee: EmployeeForm): void => {
    setEditingEmployee(employee);
    setIsEditModalOpen(true);
  };

  // Delete commented out — use active/inactive toggle instead
  // const handleDelete = async (employeeId: string): Promise<void> => {
  //   if (await toast.confirm('This action cannot be undone.', 'Delete employee?', 'Delete')) {
  //     try {
  //       await deleteEmployee(Number(employeeId));
  //       await loadEmployees();
  //       toast.success('Employee deleted successfully!');
  //     } catch (error) {
  //       console.error("Failed to delete employee:", error);
  //       toast.error('Failed to delete employee. Please try again.');
  //     }
  //   }
  // };

  const handleSuccess = async (): Promise<void> => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    const wasEditing = !!editingEmployee;
    setEditingEmployee(null);
    await loadEmployees();
    toast.success(wasEditing ? 'Employee updated successfully!' : 'Employee added successfully!');
  };


  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log("File upload:", file.name);
    toast.info("File upload functionality will be implemented with actual CSV parsing.");

    event.target.value = '';
  };

  return (
    <PageLayout onLogout={onLogout} currentPage="employees" onNavigate={onNavigate}>
        <div className="p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Employee Management</h1>
                <p className="text-blue-600 mt-1">Manage your team members and their information</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => document.getElementById('import-employees-input')?.click()}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </button>
                <input
                  type="file"
                  id="import-employees-input"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />

                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Employee
                </button>
              </div>
            </div>

            {/* Employee List */}
            <EmployeeList
              employees={employees}
              isLoading={isLoading}
              onEdit={(emp) => {
                handleEdit(mapEmployeeMainToForm(emp))
              }}
              onViewExpenses={(empId) => {
                window.location.href = `/expenses?employee_id=${empId}`;
              }}
              pagination={pagination}
              onPageChange={(page) => loadEmployees(page, serverFilters)}
              serverFilters={serverFilters}
              onServerFilterChange={handleServerFilterChange}
              onStatusToggled={() => loadEmployees(pagination.current_page, serverFilters)}
            />

            {/* Add Employee Modal */}
            <EmployeeFormModal
              isEdit={false}
              isOpen={isAddModalOpen}
              onOpenChange={setIsAddModalOpen}
              onSuccess={handleSuccess}
              title="Add New Employee"
              submitButtonText="Add Employee"
            />

            {/* Edit Employee Modal */}
            <EmployeeFormModal
              isEdit={true}
              isOpen={isEditModalOpen}
              onOpenChange={setIsEditModalOpen}
              employee={editingEmployee}
              onSuccess={handleSuccess}
              title={`${editingEmployee?.name || 'Edit'} - Employee Information`}
              submitButtonText="Update Employee"
            />
          </div>
        </div>
    </PageLayout>
  );
};

export default EmployeesPage;
